/* eslint-disable import/no-cycle */
import { PayloadAction, createAction, createSlice } from '@reduxjs/toolkit'
import { ResetPasswordState } from 'typings/reduxStore'
import { OriginalRootState } from 'redux/rootReducer'
import { ServerError } from 'typings/serverError'
import responseCodes from 'const/responseCodes'
import { ResetPasswordEligibilityCheckResponse } from 'services/resetPassword/typings'
import { ResponseStatus } from 'syf-transmit-mfe/dist/typings/transmit'
import { fetchAuthToken } from 'redux/authToken/async-thunks'
import {
  getResetPasswordEligibilityCheck,
  resetPassword,
  updatePasswordPostLogin
} from './async-thunks'

export const initState: ResetPasswordState = {
  sessionId: '',
  jwtToken: '',
  status: 'initial',
  isPasswordUpdatedSuccess: false,
  isPreviousPasswordUsed: false,
  isPasswordMismatch: false,
  isAccountError: false,
  isUserIdError: false,
  error: false,
  errorMessage: '',
  userId: '',
  errorCode: ''
}

const resetPasswordSlice = createSlice({
  name: 'resetPasswordSlice',
  initialState: initState,
  reducers: {
    updateResetPasswordInfo: (
      state: ResetPasswordState,
      action: PayloadAction<ResetPasswordState>
    ) => {
      return {
        ...state,
        ...action.payload
      }
    },
    initResetPasswordState: () => {
      return initState
    },
    onResetPasswordTransmitError: (
      state: ResetPasswordState,
      action: PayloadAction<ResponseStatus['responseCode']>
    ) => {
      state.error = true
      state.errorCode = action.payload
      state.errorMessage = ''
    },
    fetchAuthTokenSuccess: (state: ResetPasswordState) => {
      state.error = false
    }
  },
  extraReducers(builder) {
    builder
      .addCase(
        getResetPasswordEligibilityCheck.fulfilled,
        (
          state: ResetPasswordState,
          action: PayloadAction<ResetPasswordEligibilityCheckResponse>
        ) => {
          state.sessionId = action.payload.sessionId
          state.jwtToken = action.payload.jwtToken
          state.error = false
          state.errorMessage = ''
        }
      )
      .addCase(
        getResetPasswordEligibilityCheck.rejected,
        (state: ResetPasswordState, action: PayloadAction<unknown>) => {
          const error = action.payload as ServerError
          const { businessErrors = [] } = error.response || {}
          const { code: errorCode = '' } = businessErrors[0]

          state.errorCode = errorCode
          if (errorCode === responseCodes.userIdMismatch) {
            state.isUserIdError = true
          } else if (
            errorCode === responseCodes.accountNotFound ||
            errorCode === responseCodes.accountTypeDenied
          ) {
            state.isAccountError = true
          }
        }
      )
      .addCase(updatePasswordPostLogin.pending, (state: ResetPasswordState) => {
        state.status = 'pending'
      })
      .addCase(
        updatePasswordPostLogin.fulfilled,
        (state: ResetPasswordState) => {
          state.status = 'fulfilled'

          state.isPasswordUpdatedSuccess = true
          state.error = false
          state.errorMessage = ''
          state.errorCode = ''
        }
      )
      .addCase(
        updatePasswordPostLogin.rejected,
        (state: ResetPasswordState, action) => {
          const error = action.payload as ServerError
          const { businessErrors = [], message } = error.response

          const { passwordUsage, passwordMismatch } = responseCodes

          state.status = 'rejected'

          state.isPasswordUpdatedSuccess = false
          state.error = true
          state.errorMessage = message
          state.errorCode = businessErrors[0]?.code
          state.isPreviousPasswordUsed =
            businessErrors[0]?.code === passwordUsage
          state.isPasswordMismatch =
            businessErrors[0]?.code === passwordMismatch
        }
      )
      .addCase(resetPassword.fulfilled, (state: ResetPasswordState) => {
        state.isPasswordUpdatedSuccess = true
        state.error = false
        state.errorMessage = ''
      })
      .addCase(
        resetPassword.rejected,
        (state: ResetPasswordState, action: PayloadAction<unknown>) => {
          const error = action.payload as ServerError
          if (error.statusCode === 422) {
            state.isPreviousPasswordUsed = true
          }
          state.isPasswordUpdatedSuccess = false
          state.error = true
        }
      )
      .addCase(fetchAuthToken.rejected, (state: ResetPasswordState) => {
        state.isPasswordUpdatedSuccess = false
        state.error = true
      })
  }
})

/**
 * Action occurs when user cancels password update (firstTimeLogon flag U)
 */
export const userCancelledUpdatePassword = createAction(
  'resetPasswordSlice/userCancelledUpdatePassword'
)

export const {
  initResetPasswordState,
  onResetPasswordTransmitError,
  updateResetPasswordInfo,
  fetchAuthTokenSuccess
} = resetPasswordSlice.actions

export const selectSessionIdFromEligibility = (state: OriginalRootState) =>
  state.resetPassword.sessionId

export const selectJwtToken = (state: OriginalRootState) =>
  state.resetPassword.jwtToken

export const selectResetPasswordStatus = (state: OriginalRootState) =>
  state.resetPassword.status
export const selectResetPasswordErrorCode = (state: OriginalRootState) =>
  state.resetPassword.errorCode

export default resetPasswordSlice.reducer
